// Porte do schema Postgres original (supabase/migrations/, mantido no
// historico do git) para Firestore. Sem Cloud Functions (Firebase Spark —
// ver projetos/cafelabs-admin.md no Mind), a logica que antes vivia em
// triggers SECURITY DEFINER agora vive aqui: normalizacao de campos,
// checagem de supressao, gating de canal_permitido/WhatsApp, e o historico
// append-only de etapa. Nada disso e mais garantido pelo servidor — e
// responsabilidade deste arquivo ser o UNICO caminho de escrita usado pelo
// resto do app (ver firestore.rules para o que a troca de escopo aceita).
import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  limit as fsLimit,
  orderBy,
  query,
  updateDoc,
  where,
  type DocumentData,
  type QueryDocumentSnapshot,
} from "firebase/firestore";
import { auth, db } from "./firebase";
import { normalizarTelefone, normalizarTexto } from "./normalizar";
import { limitesDeHojeSaoPaulo } from "./format";
import type {
  AdiarLeadResultado,
  AppUser,
  ContactAttempt,
  ContactAttemptComAutor,
  ImportItem,
  Lead,
  LeadComResponsavel,
  LeadStage,
  LeadStageEventComAutor,
  Motivo,
  MotivoCategoria,
} from "./types";

const leadsCol = collection(db, "leads");
const contactAttemptsCol = collection(db, "contact_attempts");
const leadStageEventsCol = collection(db, "lead_stage_events");
const naoContatarCol = collection(db, "nao_contatar");
const appUsersCol = collection(db, "app_users");
const motivosCol = collection(db, "motivos");
const importItemsCol = collection(db, "import_items");

function docToLead(snap: QueryDocumentSnapshot<DocumentData>): Lead {
  return { id: snap.id, ...(snap.data() as Omit<Lead, "id">) };
}

function uidAtual(): string | null {
  return auth.currentUser?.uid ?? null;
}

// -----------------------------------------------------------------------------
// Anexa responsavel/lote (embeds do PostgREST original) via join client-side
// — dataset pequeno o bastante (prospeccao de 2-3 pessoas) para nao precisar
// de mais que 1 leitura extra de app_users no total.
// -----------------------------------------------------------------------------
async function anexarResponsavelELote(leads: Lead[]): Promise<LeadComResponsavel[]> {
  const usuarios = await fetchAppUsers();
  const porId = new Map(usuarios.map((u) => [u.id, u]));

  const batchIds = [...new Set(leads.map((l) => l.batch_id).filter((v): v is string => Boolean(v)))];
  const lotesPorId = new Map<string, { nome: string }>();
  await Promise.all(
    batchIds.map(async (batchId) => {
      const snap = await getDoc(doc(db, "import_batches", batchId));
      if (snap.exists()) lotesPorId.set(batchId, { nome: (snap.data() as { nome: string }).nome });
    }),
  );

  return leads.map((lead) => ({
    ...lead,
    responsavel: lead.responsavel_id ? { nome: porId.get(lead.responsavel_id)?.nome ?? "—" } : null,
    lote: lead.batch_id ? (lotesPorId.get(lead.batch_id) ?? null) : null,
  }));
}

// -----------------------------------------------------------------------------
// Higiene de campos livres — porte de tg_leads_before_write, bloco 1.
// -----------------------------------------------------------------------------
function higienizarTags(tags: string[]): string[] {
  const vistas = new Set<string>();
  const limpas: string[] = [];
  for (const raw of tags) {
    const v = raw.trim().toLowerCase().slice(0, 40);
    if (v !== "" && !vistas.has(v)) {
      vistas.add(v);
      limpas.push(v);
    }
  }
  return limpas.sort();
}

// -----------------------------------------------------------------------------
// Lista de supressao — porte de public.esta_suprimido(). Duas consultas
// independentes (telefone OU empresa+cidade) em vez de uma query OR
// composta: mais simples de indexar, e o volume de dados nao pede a
// otimizacao de uma leitura so.
// -----------------------------------------------------------------------------
export async function estaSuprimido(
  telefone: string | null,
  empresa: string | null,
  cidade: string | null,
): Promise<boolean> {
  const telNorm = normalizarTelefone(telefone);
  const empNorm = normalizarTexto(empresa);
  const cidNorm = normalizarTexto(cidade);

  if (telNorm) {
    const r = await getDocs(
      query(naoContatarCol, where("telefone_normalizado", "==", telNorm), where("revogado_em", "==", null)),
    );
    if (!r.empty) return true;
  }
  if (empNorm && cidNorm) {
    const r = await getDocs(
      query(
        naoContatarCol,
        where("empresa_normalizada", "==", empNorm),
        where("cidade_normalizada", "==", cidNorm),
        where("revogado_em", "==", null),
      ),
    );
    if (!r.empty) return true;
  }
  return false;
}

// -----------------------------------------------------------------------------
// Historico de etapa — porte de tg_lead_stage_event (AFTER INSERT/UPDATE OF etapa).
// -----------------------------------------------------------------------------
async function escreverEventoEtapa(
  leadId: string,
  de: LeadStage | null,
  para: LeadStage,
  motivoId: string | null,
  motivoOutro: string | null,
): Promise<void> {
  await addDoc(leadStageEventsCol, {
    lead_id: leadId,
    de,
    para,
    motivo_id: motivoId,
    motivo_outro: motivoOutro,
    autor_id: uidAtual(),
    ocorreu_em: new Date().toISOString(),
  });
}

// -----------------------------------------------------------------------------
// Atividade derivada — porte de public.recalcular_atividade_lead(). Chamada
// depois de qualquer escrita em contact_attempts (criar ou anular).
// -----------------------------------------------------------------------------
export async function recalcularAtividadeLead(leadId: string): Promise<void> {
  const leadSnap = await getDoc(doc(db, "leads", leadId));
  if (!leadSnap.exists()) return;
  const lead = docToLead(leadSnap as QueryDocumentSnapshot<DocumentData>);

  const r = await getDocs(
    query(contactAttemptsCol, where("lead_id", "==", leadId), where("anulado_em", "==", null)),
  );
  const tentativas = r.docs.map((d) => d.data() as ContactAttempt);

  const ultimaTentativa = tentativas
    .map((t) => t.ocorreu_em)
    .sort()
    .at(-1);
  const respostas = tentativas.filter((t) => ["atendeu", "pediu_retorno", "recusou"].includes(t.resultado));
  const ultimaResposta = respostas
    .map((t) => t.ocorreu_em)
    .sort()
    .at(-1);
  const houveResposta = tentativas.some((t) => t.resultado === "atendeu" || t.resultado === "pediu_retorno");
  const semRespostaDesde = tentativas.filter((t) => !ultimaResposta || t.ocorreu_em > ultimaResposta);

  const canalAtual = lead.canal_permitido;
  const novoCanal =
    lead.nao_contatar || lead.etapa === "recusou"
      ? "nenhum"
      : houveResposta
        ? "ligacao_e_whatsapp"
        : canalAtual;

  await updateDoc(doc(db, "leads", leadId), {
    ultima_tentativa_em: ultimaTentativa ?? null,
    ultima_resposta_em: ultimaResposta ?? null,
    tentativas_total: tentativas.length,
    tentativas_sem_resposta: semRespostaDesde.length,
    data_ultima_interacao:
      ultimaTentativa && ultimaTentativa > lead.data_ultima_interacao ? ultimaTentativa : lead.data_ultima_interacao,
    canal_permitido: novoCanal,
  });
}

// =============================================================================
// API publica — mesmas assinaturas do leads.ts original (Supabase), pra nao
// exigir mudanca nos componentes que consomem este modulo.
// =============================================================================

export interface FilaHojeResultado {
  atrasados: LeadComResponsavel[];
  hoje: LeadComResponsavel[];
}

export async function fetchFilaHoje(): Promise<FilaHojeResultado> {
  const { fim } = limitesDeHojeSaoPaulo();

  const r = await getDocs(
    query(
      leadsCol,
      where("arquivado_em", "==", null),
      where("nao_contatar", "==", false),
      where("proximo_contato_em", "<=", fim.toISOString()),
      orderBy("proximo_contato_em", "asc"),
    ),
  );

  const leads = await anexarResponsavelELote(r.docs.map(docToLead));
  const agora = Date.now();
  const atrasados = leads.filter((l) => l.proximo_contato_em && new Date(l.proximo_contato_em).getTime() < agora);
  const hoje = leads.filter((l) => l.proximo_contato_em && new Date(l.proximo_contato_em).getTime() >= agora);

  return { atrasados, hoje };
}

export async function fetchKanbanLeads(incluirArquivados: boolean): Promise<LeadComResponsavel[]> {
  const r = incluirArquivados
    ? await getDocs(query(leadsCol, orderBy("atualizado_em", "desc")))
    : await getDocs(query(leadsCol, where("arquivado_em", "==", null), orderBy("atualizado_em", "desc")));
  return anexarResponsavelELote(r.docs.map(docToLead));
}

export async function fetchLeadById(id: string): Promise<LeadComResponsavel> {
  const snap = await getDoc(doc(db, "leads", id));
  if (!snap.exists()) throw new Error("Lead não encontrado (ou sem acesso).");
  const [comEmbed] = await anexarResponsavelELote([docToLead(snap as QueryDocumentSnapshot<DocumentData>)]);
  return comEmbed;
}

export interface LeadSearchFiltros {
  busca?: string;
  etapa?: LeadStage | "todas";
  responsavelId?: string | "todos";
  incluirArquivados?: boolean;
}

/** Busca com so 1 filtro estruturado no Firestore (arquivado_em) — o resto
 * (etapa, responsavel, texto livre) e filtrado em JS depois de buscar.
 * Evita a explosao combinatoria de indices compostos que os filtros
 * opcionais desta tela pediriam; viavel no volume de uma prospeccao de 2-3
 * pessoas (nao para milhares de leads). */
export async function searchLeads(filtros: LeadSearchFiltros): Promise<LeadComResponsavel[]> {
  const r = filtros.incluirArquivados
    ? await getDocs(query(leadsCol, orderBy("criado_em", "desc"), fsLimit(500)))
    : await getDocs(
        query(leadsCol, where("arquivado_em", "==", null), orderBy("criado_em", "desc"), fsLimit(500)),
      );

  let leads = r.docs.map(docToLead);

  if (filtros.etapa && filtros.etapa !== "todas") {
    leads = leads.filter((l) => l.etapa === filtros.etapa);
  }
  if (filtros.responsavelId && filtros.responsavelId !== "todos") {
    leads = leads.filter((l) => l.responsavel_id === filtros.responsavelId);
  }
  if (filtros.busca && filtros.busca.trim() !== "") {
    const termo = filtros.busca.trim().toLowerCase();
    leads = leads.filter((l) =>
      [l.empresa, l.cidade, l.pessoa_contato, l.telefone].some((campo) =>
        (campo ?? "").toLowerCase().includes(termo),
      ),
    );
  }

  return anexarResponsavelELote(leads);
}

/** Campos graváveis pelo cliente (equivalente aos GRANT UPDATE de coluna do
 * schema original — as colunas derivadas ficam de fora de propósito). */
export type LeadPatch = Partial<{
  empresa: string;
  ramo: string | null;
  cidade: string | null;
  uf: string | null;
  telefone: string | null;
  pessoa_contato: string | null;
  email: string | null;
  site: string | null;
  etapa: LeadStage;
  prioridade: Lead["prioridade"];
  responsavel_id: string | null;
  proximo_contato_em: string | null;
  motivo_id: string | null;
  motivo_outro: string | null;
  arquivado_em: string | null;
  arquivado_motivo: string | null;
  tags: string[];
  canal_permitido: Lead["canal_permitido"];
  reuniao_em: string | null;
  nota_fechamento: string | null;
}>;

export async function updateLead(id: string, patch: LeadPatch): Promise<Lead> {
  const antesSnap = await getDoc(doc(db, "leads", id));
  if (!antesSnap.exists()) throw new Error("Lead não encontrado (ou sem acesso).");
  const antes = docToLead(antesSnap as QueryDocumentSnapshot<DocumentData>);

  const escrita: Record<string, unknown> = { ...patch };

  if (patch.tags) escrita.tags = higienizarTags(patch.tags);
  if (patch.uf !== undefined) escrita.uf = patch.uf ? patch.uf.toUpperCase() : null;

  // Recalcula campos normalizados quando a identidade muda — equivalente as
  // colunas GENERATED do Postgres.
  if (patch.telefone !== undefined) escrita.telefone_normalizado = normalizarTelefone(patch.telefone);
  if (patch.empresa !== undefined) escrita.empresa_normalizada = normalizarTexto(patch.empresa);
  if (patch.cidade !== undefined) escrita.cidade_normalizada = normalizarTexto(patch.cidade);

  // Bloqueio sempre vence — porte do bloco 2 do trigger.
  const suprimido = await estaSuprimido(
    patch.telefone !== undefined ? patch.telefone : antes.telefone,
    patch.empresa !== undefined ? patch.empresa : antes.empresa,
    patch.cidade !== undefined ? patch.cidade : antes.cidade,
  );
  escrita.nao_contatar = suprimido;

  const novaEtapa = patch.etapa ?? antes.etapa;
  if (suprimido || novaEtapa === "recusou") {
    escrita.canal_permitido = "nenhum";
  } else if (patch.canal_permitido === "ligacao_e_whatsapp" && antes.canal_permitido !== "ligacao_e_whatsapp") {
    const r = await getDocs(
      query(
        contactAttemptsCol,
        where("lead_id", "==", id),
        where("anulado_em", "==", null),
        where("resultado", "in", ["atendeu", "pediu_retorno"]),
      ),
    );
    if (r.empty) {
      throw new Error(
        "WhatsApp só pode ser liberado depois de uma resposta registrada do lead " +
          '(registre a tentativa com resultado "atendeu" ou "pediu_retorno").',
      );
    }
  }

  if (patch.etapa !== undefined && patch.etapa !== antes.etapa) {
    escrita.data_ultima_interacao = new Date().toISOString();
  }
  escrita.atualizado_em = new Date().toISOString();

  await updateDoc(doc(db, "leads", id), escrita);

  if (patch.etapa !== undefined && patch.etapa !== antes.etapa) {
    await escreverEventoEtapa(id, antes.etapa, patch.etapa, patch.motivo_id ?? null, patch.motivo_outro ?? null);
  }

  const depoisSnap = await getDoc(doc(db, "leads", id));
  return docToLead(depoisSnap as QueryDocumentSnapshot<DocumentData>);
}

export interface NovoLeadIndividual {
  empresa: string;
  ramo?: string | null;
  cidade?: string | null;
  uf?: string | null;
  telefone?: string | null;
  pessoa_contato?: string | null;
  email?: string | null;
  site?: string | null;
  prioridade?: Lead["prioridade"];
  responsavel_id?: string | null;
  tags?: string[];
  origem_fonte: string;
  origem_detalhe: string;
  origem_coletada_em: string;
}

export async function createLead(input: NovoLeadIndividual): Promise<Lead> {
  if (await estaSuprimido(input.telefone ?? null, input.empresa, input.cidade ?? null)) {
    throw new Error(
      `Lead bloqueado: "${input.empresa}" consta na lista de não contatar. Criação impedida.`,
    );
  }

  const agora = new Date().toISOString();
  const novo = {
    empresa: input.empresa,
    ramo: input.ramo ?? null,
    cidade: input.cidade ?? null,
    uf: input.uf ? input.uf.toUpperCase() : null,
    telefone: input.telefone ?? null,
    pessoa_contato: input.pessoa_contato ?? null,
    email: input.email ?? null,
    site: input.site ?? null,

    telefone_normalizado: normalizarTelefone(input.telefone),
    empresa_normalizada: normalizarTexto(input.empresa),
    cidade_normalizada: normalizarTexto(input.cidade),

    origem_fonte: input.origem_fonte,
    origem_detalhe: input.origem_detalhe,
    origem_coletada_em: input.origem_coletada_em,
    batch_id: null,

    etapa: "novo" as LeadStage,
    prioridade: input.prioridade ?? "media",
    responsavel_id: input.responsavel_id ?? null,
    proximo_contato_em: null,
    motivo_id: null,
    motivo_outro: null,

    arquivado_em: null,
    arquivado_motivo: null,

    tags: higienizarTags(input.tags ?? []),

    // Lead novo nunca nasce com WhatsApp liberado.
    canal_permitido: "somente_ligacao" as const,
    nao_contatar: false,

    ultima_tentativa_em: null,
    ultima_resposta_em: null,
    data_ultima_interacao: agora,
    tentativas_total: 0,
    tentativas_sem_resposta: 0,

    criado_por: uidAtual(),
    criado_em: agora,
    atualizado_em: agora,

    reuniao_em: null,
    nota_fechamento: null,
    google_event_id: null,
  };

  const ref = await addDoc(leadsCol, novo);
  await escreverEventoEtapa(ref.id, null, "novo", null, null);

  return { id: ref.id, ...novo };
}

export async function fetchImportItems(batchId: string): Promise<ImportItem[]> {
  const r = await getDocs(
    query(importItemsCol, where("batch_id", "==", batchId), orderBy("linha_num", "asc")),
  );
  return r.docs.map((d) => ({ id: d.id, ...(d.data() as Omit<ImportItem, "id">) }));
}

export async function fetchMotivos(categoria?: MotivoCategoria): Promise<Motivo[]> {
  const r = categoria
    ? await getDocs(query(motivosCol, where("categoria", "==", categoria), where("ativo", "==", true)))
    : await getDocs(query(motivosCol, where("ativo", "==", true)));
  return r.docs
    .map((d) => ({ id: d.id, ...(d.data() as Omit<Motivo, "id">) }))
    .sort((a, b) => a.ordem - b.ordem);
}

/** Supressão ativa (não revogada) originada por este lead — mesmo escopo do
 * original (não cruza com supressão criada por OUTRO lead de mesma
 * identidade; fora do escopo do v1). */
export async function fetchSupressaoAtivaPorLead(leadId: string): Promise<{ id: string } | null> {
  const r = await getDocs(
    query(
      naoContatarCol,
      where("origem_lead_id", "==", leadId),
      where("revogado_em", "==", null),
      orderBy("marcado_em", "desc"),
      fsLimit(1),
    ),
  );
  return r.empty ? null : { id: r.docs[0].id };
}

export async function fetchAppUsers(): Promise<AppUser[]> {
  const r = await getDocs(query(appUsersCol, where("ativo", "==", true)));
  return r.docs
    .map((d) => ({ id: d.id, ...(d.data() as Omit<AppUser, "id">) }))
    .sort((a, b) => a.nome.localeCompare(b.nome));
}

export async function fetchContactAttempts(leadId: string): Promise<ContactAttemptComAutor[]> {
  const [r, usuarios] = await Promise.all([
    getDocs(query(contactAttemptsCol, where("lead_id", "==", leadId), orderBy("ocorreu_em", "desc"))),
    fetchAppUsers(),
  ]);
  const porId = new Map(usuarios.map((u) => [u.id, u]));
  return r.docs.map((d) => {
    const data = d.data() as Omit<ContactAttempt, "id">;
    return { id: d.id, ...data, autor: porId.get(data.autor_id) ? { nome: porId.get(data.autor_id)!.nome } : null };
  });
}

/** "Dias na etapa" deriva do evento mais recente de lead_stage_events por
 * lead (por construção, `para` sempre é a etapa atual do lead). */
export async function fetchDataEntradaEtapaPorLead(leadIds: string[]): Promise<Map<string, string>> {
  if (leadIds.length === 0) return new Map();

  const mapa = new Map<string, string>();
  // "in" do Firestore aceita no maximo 30 valores por consulta — faz em lotes.
  for (let i = 0; i < leadIds.length; i += 30) {
    const lote = leadIds.slice(i, i + 30);
    const r = await getDocs(
      query(leadStageEventsCol, where("lead_id", "in", lote), orderBy("ocorreu_em", "desc")),
    );
    for (const d of r.docs) {
      const data = d.data() as { lead_id: string; ocorreu_em: string };
      if (!mapa.has(data.lead_id)) mapa.set(data.lead_id, data.ocorreu_em);
    }
  }
  return mapa;
}

export async function fetchLeadStageEvents(leadId: string): Promise<LeadStageEventComAutor[]> {
  const [r, usuarios, motivos] = await Promise.all([
    getDocs(query(leadStageEventsCol, where("lead_id", "==", leadId), orderBy("ocorreu_em", "desc"))),
    fetchAppUsers(),
    fetchMotivos(),
  ]);
  const porUsuario = new Map(usuarios.map((u) => [u.id, u]));
  const porMotivo = new Map(motivos.map((m) => [m.id, m]));
  return r.docs.map((d) => {
    const data = d.data() as {
      lead_id: string;
      de: LeadStage | null;
      para: LeadStage;
      motivo_id: string | null;
      motivo_outro: string | null;
      autor_id: string | null;
      ocorreu_em: string;
    };
    return {
      id: d.id,
      ...data,
      autor: data.autor_id && porUsuario.get(data.autor_id) ? { nome: porUsuario.get(data.autor_id)!.nome } : null,
      motivo:
        data.motivo_id && porMotivo.get(data.motivo_id) ? { rotulo: porMotivo.get(data.motivo_id)!.rotulo } : null,
    };
  });
}

/** Registra uma tentativa de contato — porte de trg_contact_attempt_before +
 * trg_contact_attempt_after (validação de bloqueio/canal + recálculo de
 * atividade em seguida). Usada por tentativas.ts. */
export async function createContactAttempt(input: {
  leadId: string;
  canal: ContactAttempt["canal"];
  resultado: ContactAttempt["resultado"];
  autorId: string;
  nota: string | null;
}): Promise<ContactAttempt> {
  const leadSnap = await getDoc(doc(db, "leads", input.leadId));
  if (!leadSnap.exists()) throw new Error(`Lead ${input.leadId} não encontrado.`);
  const lead = docToLead(leadSnap as QueryDocumentSnapshot<DocumentData>);

  if (lead.nao_contatar) {
    throw new Error("Contato bloqueado: o lead consta na lista de não contatar.");
  }
  if (lead.canal_permitido === "nenhum" && input.resultado !== "recusou") {
    throw new Error(
      "Contato bloqueado para este lead (canal não permitido). O lead recusou ou está suprimido: " +
        "não há novo contato previsto.",
    );
  }
  if (input.canal === "whatsapp" && lead.canal_permitido !== "ligacao_e_whatsapp") {
    throw new Error(
      "WhatsApp não liberado para este lead. Contato frio por WhatsApp viola os Termos do WhatsApp Business.",
    );
  }

  const novo = {
    lead_id: input.leadId,
    ocorreu_em: new Date().toISOString(),
    canal: input.canal,
    resultado: input.resultado,
    autor_id: input.autorId,
    nota: input.nota,
    anulado_em: null,
    anulado_por: null,
    anulado_motivo: null,
    criado_em: new Date().toISOString(),
  };
  const ref = await addDoc(contactAttemptsCol, novo);
  await recalcularAtividadeLead(input.leadId);

  return { id: ref.id, ...novo };
}

/** Anula uma tentativa (correção de engano) — nunca DELETE, ver comentário
 * da coleção contact_attempts em firestore.rules. */
export async function anularContactAttempt(
  attemptId: string,
  autorId: string,
  motivo: string,
): Promise<void> {
  const snap = await getDoc(doc(db, "contact_attempts", attemptId));
  if (!snap.exists()) throw new Error(`Tentativa ${attemptId} não encontrada.`);
  const data = snap.data() as ContactAttempt;
  if (data.anulado_em) throw new Error("Esta tentativa já foi anulada.");

  await updateDoc(doc(db, "contact_attempts", attemptId), {
    anulado_em: new Date().toISOString(),
    anulado_por: autorId,
    anulado_motivo: motivo,
  });
  await recalcularAtividadeLead(data.lead_id);
}

/** Exclui um lead (delete de verdade — sem Cloud Function pra centralizar
 * isso, ver lib/lgpd.ts pro caminho completo de exclusão LGPD que também
 * apaga tentativas/histórico em cascata). Uso interno. */
export async function excluirLeadBruto(leadId: string): Promise<void> {
  await deleteDoc(doc(db, "leads", leadId));
}

/** Adiamento rápido ("snooze") — porte de adiar_lead(). Falha alto em vez de
 * filtrar em silêncio, e NÃO mexe em data_ultima_interacao (adiamento é
 * organização interna, não contato com o titular — ver comentário original
 * na migration v1.1). */
export async function adiarLead(
  leadId: string,
  opcoes: { dias: number } | { ate: string },
): Promise<AdiarLeadResultado> {
  if ("dias" in opcoes && (opcoes.dias < 1 || opcoes.dias > 365)) {
    throw new Error(`Dias deve estar entre 1 e 365 (recebido: ${opcoes.dias}).`);
  }

  const snap = await getDoc(doc(db, "leads", leadId));
  if (!snap.exists()) throw new Error(`Lead ${leadId} não encontrado.`);
  const lead = docToLead(snap as QueryDocumentSnapshot<DocumentData>);

  if (lead.nao_contatar) {
    throw new Error("Lead está na lista de não contatar: não há próximo contato a agendar.");
  }
  if (lead.arquivado_em) {
    throw new Error("Lead está arquivado. Desarquive antes de reagendar.");
  }

  const destino =
    "ate" in opcoes ? new Date(opcoes.ate) : new Date(Date.now() + opcoes.dias * 86_400_000);
  if (destino.getTime() <= Date.now()) {
    throw new Error(`A nova data de contato precisa estar no futuro (recebido: ${destino.toISOString()}).`);
  }

  await updateDoc(doc(db, "leads", leadId), {
    proximo_contato_em: destino.toISOString(),
    atualizado_em: new Date().toISOString(),
  });

  return { lead_id: leadId, empresa: lead.empresa, proximo_contato_em: destino.toISOString() };
}
